import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Flame, Nfc, Plus, Trash2, Play, CheckCircle2, AlertCircle, Loader2, ScanLine } from 'lucide-react';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { useScannerContext } from '@/contexts/ScannerContext';
import { useNFCReader } from '@/hooks/useNFCReader';
import { useChipWriter } from '@/hooks/useChipWriter';
import { toast } from 'sonner';

interface ScannedTag {
  tag_uid: string;
  entity_type: 'plotter_cut' | 'mold' | 'piece' | 'unknown';
  entity_id?: string;
  label: string;
  work_order_id?: string;
  work_order_number?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AutoclaveCycleLoader({ open, onOpenChange, onSuccess }: Props) {
  const { user } = useAuth();
  const { nfcBridge } = useScannerContext();
  const { isSupported: isMobileNfc, scanTagUid } = useNFCReader();
  const { writeChip } = useChipWriter();

  const [step, setStep] = useState<'identify' | 'load' | 'running' | 'finished'>('identify');
  const [machineId, setMachineId] = useState<string>('');
  const [recipeId, setRecipeId] = useState<string>('');
  const [scannedTags, setScannedTags] = useState<ScannedTag[]>([]);
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [cycleNumber, setCycleNumber] = useState<string | null>(null);
  const [autoclaveTagUid, setAutoclaveTagUid] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [scanningMobile, setScanningMobile] = useState(false);
  const [lastUsbReadAt, setLastUsbReadAt] = useState(0);

  const [machines, setMachines] = useState<Array<{ id: string; name: string }>>([]);
  const [recipes, setRecipes] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data: m } = await api.from('machines').select('id, name').eq('machine_type', 'autoclave').eq('is_active', true);
      setMachines((m || []) as any);
      const { data: r } = await api.from('curing_recipes').select('id, name').eq('is_active', true).order('name');
      setRecipes((r || []) as any);
    })();
  }, [open]);

  // Reset state cada vez que se abre
  useEffect(() => {
    if (open) {
      setStep('identify');
      setMachineId('');
      setRecipeId('');
      setScannedTags([]);
      setCycleId(null);
      setCycleNumber(null);
      setAutoclaveTagUid(null);
    }
  }, [open]);

  // Lookup chip → identifica entity
  const lookupTag = async (uid: string): Promise<{ tag: any; isAutoclave: boolean }> => {
    const { data: tag } = await api
      .from('nfc_tags')
      .select(`
        tag_uid, tag_type, assigned_to_machine_id, assigned_to_mold_id, assigned_to_plotter_cut_id,
        molds:assigned_to_mold_id (id, name),
        plotter_cuts:assigned_to_plotter_cut_id (id, package_code, package_name, assigned_work_order_id)
      `)
      .eq('tag_uid', uid)
      .maybeSingleRow();
    return { tag, isAutoclave: tag?.tag_type === 'autoclave' };
  };

  // Paso 1: identificar autoclave por chip
  const handleIdentifyAutoclave = async (uid: string) => {
    const { tag, isAutoclave } = await lookupTag(uid);
    if (!tag) {
      toast.error(`Chip ${uid} no registrado. Asígnalo a un autoclave en Admin Etiquetas.`);
      return;
    }
    if (!isAutoclave || !(tag as any).assigned_to_machine_id) {
      toast.error(`Chip ${uid} no está asignado a un autoclave (tag_type=${tag.tag_type}).`);
      return;
    }
    setMachineId((tag as any).assigned_to_machine_id);
    setAutoclaveTagUid(uid);
    toast.success('Autoclave identificado');
  };

  // Listener USB
  useEffect(() => {
    if (!open || !nfcBridge.lastRead || nfcBridge.lastRead.timestamp <= lastUsbReadAt) return;
    setLastUsbReadAt(nfcBridge.lastRead.timestamp);
    const uid = nfcBridge.lastRead.uid;
    if (step === 'identify') {
      handleIdentifyAutoclave(uid);
    } else if (step === 'load') {
      handleScanLoad(uid);
    }
  }, [nfcBridge.lastRead, open, step, lastUsbReadAt]);

  // Paso 2: añadir chip cargado
  const handleScanLoad = async (uid: string) => {
    if (scannedTags.some((t) => t.tag_uid === uid)) {
      toast.info(`Chip ${uid} ya está en la lista`);
      return;
    }
    const { tag, isAutoclave } = await lookupTag(uid);
    if (isAutoclave) {
      toast.error('No se puede cargar un chip de autoclave como pieza');
      return;
    }
    let entry: ScannedTag;
    if (!tag) {
      entry = { tag_uid: uid, entity_type: 'unknown', label: `Chip sin asignar (${uid.slice(-8)})` };
    } else if ((tag as any).plotter_cuts) {
      const pc = (tag as any).plotter_cuts;
      entry = {
        tag_uid: uid,
        entity_type: 'plotter_cut',
        entity_id: pc.id,
        label: `${pc.package_code}${pc.package_name ? ` — ${pc.package_name}` : ''}`,
        work_order_id: pc.assigned_work_order_id,
      };
    } else if ((tag as any).molds) {
      const m = (tag as any).molds;
      entry = { tag_uid: uid, entity_type: 'mold', entity_id: m.id, label: `Molde: ${m.name}` };
    } else {
      entry = { tag_uid: uid, entity_type: 'unknown', label: `Chip ${uid.slice(-8)}` };
    }
    setScannedTags((prev) => [...prev, entry]);
    toast.success(`Añadido: ${entry.label}`);
  };

  const handleScanMobile = async () => {
    setScanningMobile(true);
    const r = await scanTagUid();
    setScanningMobile(false);
    if (r?.serialNumber) {
      if (step === 'identify') handleIdentifyAutoclave(r.serialNumber);
      else if (step === 'load') handleScanLoad(r.serialNumber);
    }
  };

  // Paso 3: iniciar ciclo
  const handleStartCycle = async () => {
    if (!machineId) {
      toast.error('Identifica el autoclave primero');
      return;
    }
    if (scannedTags.length === 0) {
      toast.error('Carga al menos un chip al ciclo');
      return;
    }
    setLoading(true);
    try {
      // 1. Crear cycle (trigger genera cycle_number automáticamente)
      const { data: cycle, error } = await api
        .from('autoclave_cycles')
        .insert({
          machine_id: machineId,
          operator_id: user?.id || null,
          status: 'running',
          started_at: new Date().toISOString(),
        })
        .select('id, cycle_number')
        .single();
      if (error) throw error;
      const newCycleId = (cycle as any).id;
      const newCycleNumber = (cycle as any).cycle_number;
      setCycleId(newCycleId);
      setCycleNumber(newCycleNumber);

      // 2. Insertar autoclave_cycle_tags
      const tagsToInsert = scannedTags.map((t) => ({
        cycle_id: newCycleId,
        tag_uid: t.tag_uid,
        source_entity_type: t.entity_type,
        source_entity_id: t.entity_id || null,
        scanned_by: user?.id || null,
      }));
      await api.from('autoclave_cycle_tags').insert(tagsToInsert);

      // 3. Crear process_records para cada chip con work_order
      // Buscar el process_id de 'autoclave_curing'
      const { data: proc } = await api
        .from('manufacturing_processes')
        .select('id')
        .eq('name', 'autoclave_curing')
        .maybeSingleRow();
      const procId = (proc as any)?.id;
      if (procId) {
        const records = scannedTags
          .filter((t) => t.work_order_id)
          .map((t) => ({
            work_order_id: t.work_order_id,
            process_id: procId,
            machine_id: machineId,
            operator_id: user?.id || null,
            autoclave_cycle_id: newCycleId,
            status: 'in_progress',
            started_at: new Date().toISOString(),
          }));
        if (records.length > 0) {
          await api.from('process_records').insert(records);
        }
      }

      // 4. Reescribir el chip del autoclave con resumen
      if (autoclaveTagUid) {
        await writeChip({
          tagUid: autoclaveTagUid,
          autoclaveCycleId: newCycleId,
          payload: {
            ent: 'autoclave',
            cycle: newCycleNumber || undefined,
            step: 'autoclave_curing',
            status: 'in_progress',
            at: new Date().toISOString(),
          },
        });
      }

      toast.success(`Ciclo ${newCycleNumber} iniciado`);
      setStep('running');
    } catch (e: any) {
      toast.error(`Error iniciando ciclo: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Paso 4: finalizar ciclo (escribir status='completed' al chip del autoclave + reescribir chips de carga)
  const handleFinishCycle = async () => {
    if (!cycleId) return;
    setLoading(true);
    try {
      // 1. Marcar ciclo completado
      await api
        .from('autoclave_cycles')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', cycleId);

      // 2. Bulk update de process_records de este ciclo a completed
      await api
        .from('process_records')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('autoclave_cycle_id', cycleId)
        .eq('status', 'in_progress');

      // 3. Reescribir cada chip de carga con status=completed
      for (const t of scannedTags) {
        try {
          await writeChip({
            tagUid: t.tag_uid,
            autoclaveCycleId: cycleId,
            payload: {
              ent: t.entity_type === 'mold' ? 'mold' : t.entity_type === 'plotter_cut' ? 'plotter_cut' : 'piece',
              ent_id: t.entity_id,
              wo: t.work_order_number,
              step: 'autoclave_curing',
              step_seq: 9,
              status: 'completed',
              at: new Date().toISOString(),
              cycle: cycleNumber || undefined,
              op: user?.email?.split('@')[0],
            },
          });
        } catch (err: any) {
          // Continúa con el resto aunque uno falle
          console.error(`Failed writing chip ${t.tag_uid}:`, err);
        }
      }

      // 4. Reescribir chip del autoclave con status idle
      if (autoclaveTagUid) {
        await writeChip({
          tagUid: autoclaveTagUid,
          autoclaveCycleId: cycleId,
          payload: {
            ent: 'autoclave',
            cycle: cycleNumber || undefined,
            step: 'autoclave_curing',
            status: 'idle',
            at: new Date().toISOString(),
          },
        });
      }

      toast.success(`Ciclo ${cycleNumber} finalizado`);
      setStep('finished');
      onSuccess?.();
    } catch (e: any) {
      toast.error(`Error finalizando ciclo: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const removeTag = (uid: string) => {
    setScannedTags((prev) => prev.filter((t) => t.tag_uid !== uid));
  };

  const machineName = useMemo(
    () => machines.find((m) => m.id === machineId)?.name || '—',
    [machines, machineId]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            {step === 'finished' ? 'Ciclo finalizado' : `Cargar ciclo de autoclave${cycleNumber ? ` — ${cycleNumber}` : ''}`}
          </DialogTitle>
          <DialogDescription>
            {step === 'identify' && 'Escanea el chip del autoclave o selecciónalo manualmente.'}
            {step === 'load' && 'Escanea cada chip que entra al ciclo. Pulsa "Iniciar" cuando hayas terminado.'}
            {step === 'running' && 'Ciclo en curso. Cuando termine físicamente, pulsa "Finalizar".'}
            {step === 'finished' && '¡Ciclo completado y todos los chips actualizados!'}
          </DialogDescription>
        </DialogHeader>

        {/* Identify */}
        {step === 'identify' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={handleScanMobile} disabled={scanningMobile || !isMobileNfc} variant="outline" className="flex-1">
                {scanningMobile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Nfc className="w-4 h-4 mr-2" />}
                Escanear chip del autoclave
              </Button>
            </div>
            {nfcBridge.isReaderReady && <p className="text-xs text-green-600">USB listo — acerca el chip al lector</p>}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">o</span>
              <hr className="flex-1" />
            </div>
            <div>
              <Label>Selección manual del autoclave</Label>
              <Select value={machineId} onValueChange={setMachineId}>
                <SelectTrigger><SelectValue placeholder="Elegir autoclave..." /></SelectTrigger>
                <SelectContent>
                  {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Receta de curado (opcional)</Label>
              <Select value={recipeId} onValueChange={setRecipeId}>
                <SelectTrigger><SelectValue placeholder="Sin receta" /></SelectTrigger>
                <SelectContent>
                  {recipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" disabled={!machineId} onClick={() => setStep('load')}>
              Continuar a carga →
            </Button>
          </div>
        )}

        {/* Load */}
        {step === 'load' && (
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-4 space-y-2">
                <p className="text-sm text-muted-foreground">Autoclave: <strong>{machineName}</strong></p>
                {nfcBridge.isReaderReady && <p className="text-xs text-green-600">● USB listo — acerca cada chip que entra al ciclo</p>}
                {isMobileNfc && (
                  <Button onClick={handleScanMobile} disabled={scanningMobile} variant="outline" size="sm">
                    {scanningMobile ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <ScanLine className="w-3 h-3 mr-2" />}
                    Escanear con móvil
                  </Button>
                )}
              </CardContent>
            </Card>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {scannedTags.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Sin chips escaneados todavía</p>
              ) : scannedTags.map((t) => (
                <div key={t.tag_uid} className="flex items-center gap-2 p-2 rounded border bg-muted/30">
                  <Badge variant="outline" className="text-xs">{t.entity_type}</Badge>
                  <span className="flex-1 text-sm">{t.label}</span>
                  <span className="text-xs text-muted-foreground font-mono">{t.tag_uid.slice(-8)}</span>
                  <Button size="sm" variant="ghost" onClick={() => removeTag(t.tag_uid)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">{scannedTags.length} chip(s) cargado(s)</p>

            <Button className="w-full" disabled={loading || scannedTags.length === 0} onClick={handleStartCycle}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              Iniciar ciclo
            </Button>
          </div>
        )}

        {/* Running */}
        {step === 'running' && (
          <div className="space-y-4">
            <Card className="border-orange-300 bg-orange-50/50">
              <CardContent className="pt-4">
                <p className="font-semibold text-orange-700">Ciclo {cycleNumber} en curso</p>
                <p className="text-sm text-muted-foreground">{scannedTags.length} chip(s) dentro del autoclave</p>
              </CardContent>
            </Card>
            <Button className="w-full" disabled={loading} onClick={handleFinishCycle}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Finalizar ciclo y reescribir chips
            </Button>
          </div>
        )}

        {/* Finished */}
        {step === 'finished' && (
          <div className="space-y-4 text-center py-6">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
            <p className="text-lg font-semibold">Ciclo {cycleNumber} completado</p>
            <p className="text-sm text-muted-foreground">{scannedTags.length} chip(s) actualizados con status=completed</p>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="w-full">Cerrar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
